/**
 * @description Hook utilizado para manipular locais
 * @author Diego Sabino Amorim de Araújo
 * @file useLocal.js
 * @license Copyright (c) 2022 SIGEP DIGETI ITEP/RN
 */

import actionTypes from 'utils/actionTypes'
import { useDispatch } from 'react-redux'
import { useFetch } from './useFetch'

export const useLocal = () => {
  const fetch = useFetch()
  const dispatch = useDispatch()
  const listarLocal = async (id, setLocal, setProcessando) => {
    if (setProcessando) setProcessando(true)
    const response = await fetch.get(`endereco/${id}`)
    setLocal(response.data.endereco)
    if (setProcessando) setProcessando(false)
  }

  const getLocaisDeExame = async (idExameSolicitado, setProcessando) => {
    if (setProcessando) setProcessando(true)
    const response = await fetch.get(`endereco/deExameSolicitado/${idExameSolicitado}`)
    if (setProcessando) setProcessando(false)
    return response.data.enderecos
  }

  const listarLocaisDeCaso = async (idCaso, setLocais, setProcessando) => {
    try {
      if (setProcessando) setProcessando(true)
      const response = await fetch.get(`endereco/deCaso/${idCaso}`)
      setLocais(response.data.enderecos)
    } catch (err) {
      return
    } finally {
      if (setProcessando) setProcessando(false)
    }
  }

  const listarLocaisDeCasosPorVeiculo = async (idVeiculo, setLocais, setProcessando) => {
    try {
      if (setProcessando) setProcessando(true)
      const response = await fetch.get(`endereco/deCasosPorVeiculo/${idVeiculo}`)
      setLocais(response.data.enderecos)
    } catch (err) {
      return
    } finally {
      if (setProcessando) setProcessando(false)
    }
  }

  const listarLocaisDeCasoAsync = async (idCaso, setProcessando) => {
    try {
      if (setProcessando) setProcessando(true)
      const response = await fetch.get(`endereco/deCaso/${idCaso}`)

      return response.data.enderecos
    } catch (err) {
      return null
    } finally {
      if (setProcessando) setProcessando(false)
    }
  }

  const listarLocaisDeCasos = async (idsCasos, setLocais, setProcessando) => {
    try {
      if (setProcessando) setProcessando(true)
      const response = await fetch.postOpenWithoutFeedback(`endereco/listar/deCasos`, idsCasos)
      setLocais(response.data.enderecos)
    } catch (err) {
      return
    } finally {
      if (setProcessando) setProcessando(false)
    }
  }

  const listarLocaisDeCasoGlobal = async (idCaso, setProcessando) => {
    try {
      if (setProcessando) setProcessando(true)
      const response = await fetch.get(`endereco/deCaso/${idCaso}`)
      dispatch({
        type: actionTypes.SET_LOCAIS_CASO,
        locais: response.data.enderecos,
      })
    } catch (err) {
      return
    } finally {
      if (setProcessando) setProcessando(false)
    }
  }

  const cadastrarLocal = async (values, idCaso, setProcessando) => {
    try {
      if (setProcessando) setProcessando(true)
      const dados = {
        cep: values.cep,
        complemento: values.complemento,
        id_caso: idCaso,
        cidade: values.municipio ? values.municipio.nome : '',
        estado: values.estado,
        rua: values.logradouro,
        numero: values.numero,
        bairro: values.bairro,
        latitudeGps: values.latitude,
        longitudeGps: values.longitude,
      }
      await fetch.post(`/endereco/`, dados)
    } catch (err) {
      return
    } finally {
      if (setProcessando) setProcessando(false)
    }
  }

  const editarLocal = async (id, values, setProcessando) => {
    try {
      if (setProcessando) setProcessando(true)
      const dados = {
        cidade: values.municipio ? values.municipio.nome : '',
        rua: values.logradouro,
        numero: values.numero,
        bairro: values.bairro,
        latitudeGps: values.latitude,
        longitudeGps: values.longitude,
      }
      await fetch.put(`/endereco/${id}`, dados)
    } catch (err) {
      return
    } finally {
      if (setProcessando) setProcessando(false)
    }
  }

  const excluirLocal = async (id, setProcessando) => {
    try {
      if (setProcessando) setProcessando(true)
      await fetch.del(`/endereco/${id}`)
    } catch (err) {
      return
    } finally {
      if (setProcessando) setProcessando(false)
    }
  }

  const listarCidadesRNAsync = async (setProcessando) => {
    try {
      if (setProcessando) setProcessando(true)
      const response = await fetch.get(`https://servicodados.ibge.gov.br/api/v1/localidades/estados/RN/municipios`)
      return response.data
    } catch (err) {
      console.log('Erro ao tentar listar as cidades do Rio Grande do Norte - Conexão com API do IBGE')
      return null
    } finally {
      if (setProcessando) setProcessando(false)
    }
  }

  const listarCidadesPorEstadoAsync = async (estado) => {
    try {
      const response = await fetch.get(`https://servicodados.ibge.gov.br/api/v1/localidades/estados/${estado}/municipios`)
      return response.data
    } catch (err) {
      console.log('Erro ao tentar listar as cidades por estado do Brasil - Conexão com API do IBGE')
      return null
    }
  }

  const listaCidadesDoBrasilAsync = async () => {
    try {
      const response = await fetch.get(`https://servicodados.ibge.gov.br/api/v1/localidades/municipios`)
      return response.data
    } catch (err) {
      console.log('Erro ao tentar listar as cidades do Brasil - Conexão com API do IBGE')
      return null
    }
  }

  const listaDeEstadosDoBrasil = async () => {
    try {
      const result = await fetch.get('https://servicodados.ibge.gov.br/api/v1/localidades/estados')
      return result.data
    } catch (err) {
      console.log('Erro ao tentar listar os estados do Brasil - Conexão com API do IBGE')
    }
  }

  const listaDePaises = async () => {
    try {
      const result = await fetch.get('https://servicodados.ibge.gov.br/api/v1/localidades/paises')
      return result.data
    } catch (err) {
      throw err
    }
  }

  return {
    listarLocal,
    getLocaisDeExame,
    listarLocaisDeCaso,
    listarLocaisDeCasoGlobal,
    cadastrarLocal,
    editarLocal,
    excluirLocal,
    listarLocaisDeCasos,
    listarLocaisDeCasoAsync,
    listarCidadesRNAsync,
    listarLocaisDeCasosPorVeiculo,
    listaDeEstadosDoBrasil,
    listarCidadesPorEstadoAsync,
    listaCidadesDoBrasilAsync,
    listaDePaises,
  }
}
